import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Integration {
  id: string;
  provider: string;
  config: { subdomain?: string };
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

function api(path: string, opts?: RequestInit) {
  const token = localStorage.getItem("kverna-token");
  return fetch(path, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...opts?.headers,
    },
  });
}

export function Integrations() {
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testing, setTesting] = useState(false);

  const [apiToken, setApiToken] = useState("");
  const [subdomain, setSubdomain] = useState("");

  const load = async () => {
    const res = await api("/api/integrations");
    if (res.ok) {
      setIntegrations(await res.json());
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleTest = async () => {
    setTesting(true);
    setError("");
    setSuccess("");

    const res = await api("/api/integrations/test", {
      method: "POST",
      body: JSON.stringify({ provider: "fakturownia", apiToken, config: { subdomain } }),
    });

    const data = await res.json();
    if (data.success) {
      setSuccess(t("integrations.testOk"));
    } else {
      setError(data.error ?? t("integrations.testFailed"));
    }
    setTesting(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const res = await api("/api/integrations", {
      method: "POST",
      body: JSON.stringify({ provider: "fakturownia", apiToken, config: { subdomain } }),
    });

    if (res.ok) {
      setShowForm(false);
      setApiToken("");
      setSubdomain("");
      await load();
    } else {
      const err = await res.json();
      setError(err.error);
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    await api(`/api/integrations/${id}`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    });
    await load();
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t("integrations.confirmDelete"))) return;
    await api(`/api/integrations/${id}`, { method: "DELETE" });
    await load();
  };

  const hasFakturownia = integrations.some((i) => i.provider === "fakturownia");

  if (loading) {
    return <div className="text-center py-16 text-navy-400">{t("common.loading")}</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: "var(--font-display)" }}>
            {t("integrations.title")}
          </h1>
          <p className="text-navy-500 text-sm">{t("integrations.subtitle")}</p>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 mb-6 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-3 mb-6 text-sm">
          {success}
        </div>
      )}

      {/* Fakturownia card */}
      <div className="bg-white rounded-xl border border-navy-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold text-sm">
              F
            </div>
            <div>
              <h3 className="font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                Fakturownia
              </h3>
              <p className="text-xs text-navy-400">{t("integrations.fakturownia.desc")}</p>
            </div>
          </div>

          {hasFakturownia ? (
            <div className="flex items-center gap-3">
              {integrations.filter((i) => i.provider === "fakturownia").map((i) => (
                <React.Fragment key={i.id}>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${i.enabled ? "bg-emerald-50 text-emerald-600" : "bg-navy-100 text-navy-400"}`}>
                    {i.enabled ? t("integrations.active") : t("integrations.disabled")}
                  </span>
                  <button
                    onClick={() => handleToggle(i.id, !i.enabled)}
                    className="text-xs text-navy-400 hover:text-navy-600"
                  >
                    {i.enabled ? t("integrations.disable") : t("integrations.enable")}
                  </button>
                  <button
                    onClick={() => handleDelete(i.id)}
                    className="text-xs text-rose-400 hover:text-rose-600"
                  >
                    {t("common.delete")}
                  </button>
                </React.Fragment>
              ))}
            </div>
          ) : (
            <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm">
              {t("integrations.connect")}
            </button>
          )}
        </div>

        {hasFakturownia && (
          <div className="text-sm text-navy-500">
            <p>
              {t("integrations.subdomain")}:{" "}
              <span className="font-mono text-navy-700">
                {integrations.find((i) => i.provider === "fakturownia")?.config.subdomain}.fakturownia.pl
              </span>
            </p>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSave} className="mt-6 space-y-4 border-t border-navy-100 pt-6">
            <div>
              <label className="block text-sm text-navy-500 mb-1">{t("integrations.subdomain")}</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                  className="input"
                  placeholder="mojafirma"
                  required
                />
                <span className="text-sm text-navy-400">.fakturownia.pl</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-navy-500 mb-1">{t("integrations.apiToken")}</label>
              <input
                type="password"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                className="input w-full"
                required
              />
              <p className="text-xs text-navy-400 mt-1">{t("integrations.apiTokenNote")}</p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={handleTest} disabled={testing || !apiToken || !subdomain} className="btn btn-ghost">
                {testing ? t("integrations.testing") : t("integrations.testConnection")}
              </button>
              <button type="submit" className="btn btn-primary">{t("integrations.save")}</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-ghost">
                {t("common.cancel")}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Placeholder for future integrations */}
      <div className="bg-white rounded-xl border border-navy-100 border-dashed p-6 text-center">
        <p className="text-sm text-navy-400">{t("integrations.comingSoon")}</p>
        <p className="text-xs text-navy-300 mt-1">inFakt, wFirma, iFirma</p>
      </div>
    </div>
  );
}
